import React, { useState } from 'react';
import {
  Container,
  Tabs,
  Tab,
  Box,
  Paper,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Typography,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { apiService } from '../services/api';

const GuestSubmission = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Property Form State
  const [propertyForm, setPropertyForm] = useState({
    title: '',
    description: '',
    location: '',
    price: '',
    beds: '',
    baths: '',
    area: '',
    category: 'rent',
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    images: [],
  });

  // Expert Form State
  const [expertForm, setExpertForm] = useState({
    name: '',
    field: 'DRIVER',
    address: '',
    phone1: '',
    phone2: '',
    note: '',
    rate: '',
    email: '',
    profileImage: null,
  });

  const handlePropertyChange = (e) => {
    const { name, value } = e.target;
    setPropertyForm(prev => ({ ...prev, [name]: value }));
  };

  const handleExpertChange = (e) => {
    const { name, value } = e.target;
    setExpertForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle multiple image uploads for property
  const handlePropertyImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setPropertyForm(prev => ({
      ...prev,
      images: [...prev.images, ...newImages],
    }));
  };

  // Remove property image
  const removePropertyImage = (index) => {
    setPropertyForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  // Handle single profile image upload for expert
  const handleExpertImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setExpertForm(prev => ({
        ...prev,
        profileImage: {
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
        },
      }));
    }
  };

  // Remove expert profile image
  const removeExpertImage = () => {
    setExpertForm(prev => ({
      ...prev,
      profileImage: null,
    }));
  };

  const handleAddProperty = async () => {
    if (!propertyForm.title || !propertyForm.description || !propertyForm.price || !propertyForm.ownerName || !propertyForm.ownerPhone) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      
      // Add text fields
      formData.append('title', propertyForm.title);
      formData.append('description', propertyForm.description);
      formData.append('location', propertyForm.location);
      formData.append('price', propertyForm.price);
      formData.append('beds', propertyForm.beds);
      formData.append('baths', propertyForm.baths);
      formData.append('area', propertyForm.area);
      formData.append('category', propertyForm.category);
      formData.append('ownerName', propertyForm.ownerName);
      formData.append('ownerPhone', propertyForm.ownerPhone);
      formData.append('ownerEmail', propertyForm.ownerEmail);
      formData.append('pending', 1);
      formData.append('status', 'pending');

      // Add images
      propertyForm.images.forEach((img) => {
        formData.append(`images`, img.file);
      });

      const response = await apiService.createProperty(formData);
      if (response.success) {
        setSuccess('✅ Property submitted successfully! Admin will review and approve it soon.');
        setPropertyForm({
          title: '',
          description: '',
          location: '',
          price: '',
          beds: '',
          baths: '',
          area: '',
          category: 'rent',
          ownerName: '',
          ownerPhone: '',
          ownerEmail: '',
          images: [],
        });
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.message || 'Failed to submit property');
      }
    } catch (err) {
      setError('Error submitting property: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpert = async () => {
    if (!expertForm.name || !expertForm.phone1 || !expertForm.field) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      
      // Add text fields
      formData.append('name', expertForm.name);
      formData.append('field', expertForm.field);
      formData.append('address', expertForm.address);
      formData.append('phone1', expertForm.phone1);
      formData.append('phone2', expertForm.phone2);
      formData.append('note', expertForm.note);
      formData.append('rate', expertForm.rate);
      formData.append('email', expertForm.email);
      formData.append('pending', 1);
      formData.append('status', 'pending');

      // Add profile image
      if (expertForm.profileImage) {
        formData.append('profileImage', expertForm.profileImage.file);
      }

      const response = await apiService.createService(formData);
      if (response.success) {
        setSuccess('✅ Profile submitted successfully! Admin will review and approve it soon.');
        setExpertForm({
          name: '',
          field: 'DRIVER',
          address: '',
          phone1: '',
          phone2: '',
          note: '',
          rate: '',
          email: '',
          profileImage: null,
        });
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.message || 'Failed to submit profile');
      }
    } catch (err) {
      setError('Error submitting profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const expertCategories = [
    'DRIVER', 'ELECTRICIAN', 'CARPENTER', 'PLUMBER', 'MECHANIC',
    'DOCTOR', 'BARBER', 'GARDENER', 'COOK', 'MOTO-MECH',
    'DELIVERY-BOY', 'LABOUR', 'MUSIC-DJ', 'BAND-BAJA'
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
          📝 Submit Your Listing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Share your property or service with us. Our admin team will review and approve your submission.
        </Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="🏠 Submit Property" />
          <Tab label="👤 Become an Expert" />
          <Tab label="📋 How It Works" />
        </Tabs>
      </Box>

      {/* Submit Property Tab */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            Add Your Property
          </Typography>
          <Grid container spacing={2}>
            {/* Owner Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#667eea' }}>
                📌 Your Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Your Name *"
                name="ownerName"
                value={propertyForm.ownerName}
                onChange={handlePropertyChange}
                placeholder="e.g., Raj Kumar"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number *"
                name="ownerPhone"
                value={propertyForm.ownerPhone}
                onChange={handlePropertyChange}
                placeholder="e.g., 9876543210"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email (Optional)"
                name="ownerEmail"
                type="email"
                value={propertyForm.ownerEmail}
                onChange={handlePropertyChange}
                placeholder="e.g., raj@example.com"
              />
            </Grid>

            {/* Property Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#667eea' }}>
                🏠 Property Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Property Title *"
                name="title"
                value={propertyForm.title}
                onChange={handlePropertyChange}
                placeholder="e.g., Beautiful 2BHK Apartment"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category *"
                name="category"
                select
                value={propertyForm.category}
                onChange={handlePropertyChange}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="rent">Rent</option>
                <option value="sell">Sell</option>
                <option value="plot">Plot</option>
                <option value="commercial">Commercial</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description *"
                name="description"
                value={propertyForm.description}
                onChange={handlePropertyChange}
                placeholder="Describe your property in detail..."
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={propertyForm.location}
                onChange={handlePropertyChange}
                placeholder="e.g., अपना बिसौली, Block अपना बिसौली"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price *"
                name="price"
                type="number"
                value={propertyForm.price}
                onChange={handlePropertyChange}
                placeholder="e.g., 5000000"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Beds"
                name="beds"
                type="number"
                value={propertyForm.beds}
                onChange={handlePropertyChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Baths"
                name="baths"
                type="number"
                value={propertyForm.baths}
                onChange={handlePropertyChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Area (sq ft)"
                name="area"
                value={propertyForm.area}
                onChange={handlePropertyChange}
              />
            </Grid>

            {/* Photo Upload Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#667eea' }}>
                📸 Property Photos (Optional)
              </Typography>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '2px dashed #667eea',
                  bgcolor: '#f9f9f9',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#f0f0f0',
                    borderColor: '#764ba2',
                  },
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="property-image-upload"
                  multiple
                  type="file"
                  onChange={handlePropertyImageUpload}
                />
                <label htmlFor="property-image-upload" style={{ cursor: 'pointer', width: '100%' }}>
                  <CloudUpload sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, GIF up to 5MB each
                  </Typography>
                </label>
              </Paper>

              {/* Display uploaded images */}
              {propertyForm.images.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Uploaded Images ({propertyForm.images.length})
                  </Typography>
                  <Grid container spacing={1}>
                    {propertyForm.images.map((img, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <Card sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="140"
                            image={img.preview}
                            alt={img.name}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              p: 0.5,
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              sx={{ minWidth: 'auto', p: 0.5 }}
                              onClick={() => removePropertyImage(index)}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </Button>
                          </Box>
                          <CardContent sx={{ p: 1 }}>
                            <Typography
                              variant="caption"
                              noWrap
                              title={img.name}
                            >
                              {img.name.substring(0, 15)}...
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleAddProperty}
                disabled={loading}
                sx={{ py: 1.5, fontSize: '1rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {loading ? <CircularProgress size={24} /> : '📤 Submit Property for Review'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Become an Expert Tab */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            Share Your Professional Profile
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name *"
                name="name"
                value={expertForm.name}
                onChange={handleExpertChange}
                placeholder="e.g., Himanshu Kumar"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Service Category *"
                name="field"
                select
                value={expertForm.field}
                onChange={handleExpertChange}
                SelectProps={{
                  native: true,
                }}
              >
                {expertCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={expertForm.address}
                onChange={handleExpertChange}
                placeholder="e.g., Saruppur, Block Bisauli"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Primary Phone *"
                name="phone1"
                value={expertForm.phone1}
                onChange={handleExpertChange}
                placeholder="e.g., 9639083063"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Secondary Phone"
                name="phone2"
                value={expertForm.phone2}
                onChange={handleExpertChange}
                placeholder="e.g., 9639083064"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rate/Hourly (Optional)"
                name="rate"
                value={expertForm.rate}
                onChange={handleExpertChange}
                placeholder="e.g., ₹500/hour"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (Optional)"
                name="email"
                type="email"
                value={expertForm.email}
                onChange={handleExpertChange}
                placeholder="e.g., expert@example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="About Your Services"
                name="note"
                value={expertForm.note}
                onChange={handleExpertChange}
                placeholder="Tell customers about your experience, skills, and services..."
                multiline
                rows={3}
              />
            </Grid>

            {/* Profile Photo Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#667eea' }}>
                📸 Profile Photo (Optional)
              </Typography>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '2px dashed #f57c00',
                  bgcolor: '#f9f9f9',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#f0f0f0',
                    borderColor: '#e67e00',
                  },
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="expert-image-upload"
                  type="file"
                  onChange={handleExpertImageUpload}
                />
                <label htmlFor="expert-image-upload" style={{ cursor: 'pointer', width: '100%' }}>
                  <CloudUpload sx={{ fontSize: 48, color: '#f57c00', mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, GIF up to 5MB
                  </Typography>
                </label>
              </Paper>

              {/* Display uploaded profile image */}
              {expertForm.profileImage && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Profile Photo Preview
                  </Typography>
                  <Card sx={{ position: 'relative', maxWidth: 150 }}>
                    <CardMedia
                      component="img"
                      height="150"
                      image={expertForm.profileImage.preview}
                      alt="Profile"
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        p: 0.5,
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        sx={{ minWidth: 'auto', p: 0.5 }}
                        onClick={removeExpertImage}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </Button>
                    </Box>
                  </Card>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleAddExpert}
                disabled={loading}
                sx={{ py: 1.5, fontSize: '1rem', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}
              >
                {loading ? <CircularProgress size={24} /> : '📤 Submit Profile for Review'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* How It Works Tab */}
      {tabValue === 2 && (
        <Box>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#667eea' }}>
                ✅ How Our Approval Process Works
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, color: '#667eea', minWidth: 30 }}>1.</Typography>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>You Submit Your Listing</Typography>
                  <Typography variant="body2" color="text.secondary">Fill out the form with all property or service details along with photos</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, color: '#667eea', minWidth: 30 }}>2.</Typography>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Admin Review</Typography>
                  <Typography variant="body2" color="text.secondary">Our team reviews your submission for accuracy and authenticity</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, color: '#667eea', minWidth: 30 }}>3.</Typography>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Approval & Publishing</Typography>
                  <Typography variant="body2" color="text.secondary">Once approved, your listing goes live and is visible to all users</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography sx={{ fontWeight: 700, color: '#667eea', minWidth: 30 }}>4.</Typography>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Get Inquiries</Typography>
                  <Typography variant="body2" color="text.secondary">Start receiving messages and calls from interested customers</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#667eea' }}>
                ⏱️ Typical Timeline
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Property Listings:</strong> 24-48 hours
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Expert/Service Profiles:</strong> 12-24 hours
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                You will receive an email notification once your submission is approved.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
};

export default GuestSubmission;